import React, { useEffect, useState, useMemo } from "react";
import Report from "../../elements/Report";
import AddReportPopup from "../AddReportPopup";
import styles from "./index.module.css";
import {
  openEdit,
  openPopup,
} from "../../../store/slices/addReport/popup.slice";
import { useSelector, useDispatch } from "react-redux";
import EditReport from "../EditReport";
import DropDown from "../../elements/Dropdown";
import {
  asyncThunkDeleteFnbReport,
  asyncThunkGetAllReports,
} from "../../../store/slices/addReport/yourReports.slice";
import { pdf } from "@react-pdf/renderer";
import PdfDocument from "../../layouts/PdfDocument";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import { userAxios } from "../../../utils/axios/user.axios";
import Header from "../../elements/Header";
import PulseLoader from "react-spinners/PulseLoader";
import { toast } from "react-toastify";

const FnbReporting = () => {
  const addReportState = useSelector((store) => store.addReport); // Hide and show form.
  const yourReportsState = useSelector((store) => store.yourReports); // List of your reports

  const [downloadLoader, setDownloadLoader] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(2);
  const [selectedYear, setSelectedYear] = useState(2026);
  const [sortOrder, setSortOrder] = useState(0); // 0 for A-Z, 1 for Z-A
  const sortOptions = ["A-Z", "Z-A"]; 
  const {
    data: { store, terminal },
    loading,
  } = useSelector((store) => store.fnbConstants);
  const productYourReportState = useSelector(
    (store) => store.productYourReports
  );
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(asyncThunkGetAllReports());
  }, []);

  const handleSortChange = (value, index) => {
    // Dropdown onChange provides (value, index)
    setSortOrder(index);
  };

  const sortedReports = useMemo(() => {
    if (!yourReportsState.yourReports || yourReportsState.yourReports.length === 0) {
      return [];
    }
    // Create a new array and sort it
    const reports = [...yourReportsState.yourReports];
    return reports.sort((a, b) => {
      const nameA = (a.name || "").toUpperCase();
      const nameB = (b.name || "").toUpperCase();
      if (sortOrder === 0) {
        // A-Z: ascending
        return nameA.localeCompare(nameB);
      } else {
        // Z-A: descending
        return nameB.localeCompare(nameA);
      }
    });
  }, [yourReportsState.yourReports, sortOrder]);

  // Show form for add report.
  const showAddReport = () => dispatch(openPopup());

  // Show edit report form.
  const showEditReport = (index) =>
    dispatch(openEdit({ show: true, reportIndex: index }));

  // Delete report.
  const deleteReport = (index) => {
    dispatch(asyncThunkDeleteFnbReport(yourReportsState.yourReports[index].id));
  };

  const handleMonthYearChange = (month, year, label) => {
    console.log(`🔄 FnbReporting: Month/Year changed to: ${month}/${year} (${label})`);
    setSelectedMonth(month);
    setSelectedYear(year);
  };

  const prepareSystemReportDataFor = async (title) => {
    let response;
    if (title == productYourReportState.productYourReports[0].title) {
      response = await userAxios("/fnbproduct", {
        params: {
          store: store.join("|"),
          terminal: terminal.join("|"),
          month: selectedMonth,
          year: selectedYear,
        },
      });
    } else if (title === productYourReportState.productYourReports[1].title) {
      response = await userAxios("/fnbproduct", {
        params: {
          store: store.join("|"),
          terminal: terminal.join("|"),
          pricerange: productYourReportState.productYourReports[1].label,
          sort: "difference_high_to_low",
          month: selectedMonth,
          year: selectedYear,
        },
      });
    } else if (title === productYourReportState.productYourReports[2].title) {
      response = await userAxios("/fnbproduct", {
        params: {
          store: store.join("|"),
          terminal: terminal.join("|"),
          pricerange: productYourReportState.productYourReports[2].label,
          sort: "difference_low_to_high",
          month: selectedMonth,
          year: selectedYear,
        },
      });
    } else {
      const report = yourReportsState.yourReports.find(
        (item) => item.name === title
      );

      response = await userAxios("/fnbproduct", {
        params: {
          store: report.store.toUpperCase(),
          terminal: report.terminal.toUpperCase(),
          breakup: report.breakup.toUpperCase(),
          month: selectedMonth,
          year: selectedYear,
        },
      });
    }

    let data = response.data;
    
    // Use selected month and year for the report date
    const monthNames = ["January", "February", "March", "April", "May", "June",
                        "July", "August", "September", "October", "November", "December"];
    const reportDate = `${monthNames[selectedMonth - 1]} ${selectedYear}`;

    data = data.map((val, idx) => {
      let category;
      if (
        (
          (parseFloat(Number(val.store_price) - Number(val.average)) /
            parseFloat(val?.store_price)) *
          100
        )?.toFixed(2) <= 0
      )
        category = "positive";
      else if (
        (
          (parseFloat(Number(val.store_price) - Number(val.average)) /
            parseFloat(val?.store_price)) *
          100
        )?.toFixed(2) > 15
      )
        category = "negative";
      else category = "watch";

      let compare = val?.products_data
        ?.filter((el) => {
          // Filter out the same store and terminal combination
          const shouldExclude = el.store_name === val.store_name && el.terminal_name === val.terminal_name;
          if (shouldExclude) {
            console.log('Excluding comparison:', {
              main: { store: val.store_name, terminal: val.terminal_name },
              excluded: { store: el.store_name, terminal: el.terminal_name }
            });
          }
          return !shouldExclude;
        })
        ?.map((el) => {
          const difference = val?.store_price - el.latest_price;
          return {
            to: el?.store_name,
            terminal: el?.terminal_name,
            item: el?.name,
            price: "$" + parseFloat(el?.latest_price).toFixed(2),
            difference:
              Number(el.latest_price) - Number(val.average) < 0
                ? "-$" +
                  Math?.abs(
                    Number(el.latest_price) - Number(val.average)
                  ).toFixed(2)
                : "$" +
                  parseFloat(
                    Number(el.latest_price) - Number(val.average)
                  ).toFixed(2),
          };
        });

      // diffPercentage: (Number(product.store_price) - Number(product.average))/Number(product.store_price),
      //   difference:Number(product.store_price) - Number(product.average),

      return {
        itemNo: val.products_data.find(
          (item) =>
            item.store_name.toLowerCase() === val.store_name.toLowerCase()
        ).id,
        itemType: val?.products_data[0].type,
        description: val?.product_name,
        price: "$" + parseFloat(val?.store_price).toFixed(2),
        compare,
        comparablePrice: "$" + parseFloat(val?.average).toFixed(2),
        difference:
          Number(val.store_price) - Number(val.average) < 0
            ? "-$" +
              Math.abs(Number(val.store_price) - Number(val.average))?.toFixed(
                2
              )
            : "$" +
              parseFloat(Number(val.store_price) - Number(val.average)).toFixed(
                2
              ),
        percentage:
          (
            (parseFloat(Number(val.store_price) - Number(val.average)) /
              parseFloat(val?.store_price)) *
            100
          )?.toFixed(2) + "%",
        category,
        terminal: val?.terminal_name,
        store: val?.store_name,
      };
    });

    const finalData = [];

    data.forEach((data) => {
      // Create a unique key combining store and terminal
      const storeTerminalKey = `${data?.store}-${data?.terminal}`;
      let idx = finalData?.findIndex(
        (el) => `${el?.name}-${el?.location}` === storeTerminalKey
      );

      if (idx == -1)
        finalData.push({
          name: data?.store,
          location: data?.terminal,
          items: [data],
        });
      else finalData[idx]?.items?.push(data);
    });

    return { data: finalData, reportDate };
  };

  const handleDownload = async (e) => {
    setDownloadLoader(true);
    const title = e.currentTarget.dataset.title;

    const result = await prepareSystemReportDataFor(
      e?.currentTarget?.dataset?.title
    );

    if (!result.data || !result.data.length) {
      setDownloadLoader(false);
      return toast.error("No products found!");
    }

    const doc = <PdfDocument data={result.data} section={"fnb"} reportDate={result.reportDate} />;
    // Generate the PDF blob
    const blob = await pdf(doc).toBlob();

    // Create a temporary URL for the blob
    const url = URL.createObjectURL(blob);

    // Create a link element to trigger the download
    const link = document.createElement("a");
    link.href = url;
    link.download = title; // Specify the file name
    link.click(); // Trigger the download
    setDownloadLoader(false);

    // Clean up by revoking the URL after download
    URL.revokeObjectURL(url);
  };

  const handleDownloadExcel = async (e) => {
    setDownloadLoader(true);
    const title = e.currentTarget.dataset.title;
    const result = await prepareSystemReportDataFor(title);

    if (!result.data || !result.data.length) {
      setDownloadLoader(false);
      return toast.error("No products found!");
    }
    
    const data = result.data;

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Process each store's data
    data.forEach((storeData) => {
      // Get all unique stores and terminals for comparison
      // const allStores = Array.from(new Set(storeData.items.map(d => d.compare.map(e=>`${e.to}-${d.terminal}`))));

      let temp = {};
      storeData.items.forEach((s) => {
        s.compare.forEach((i) => {
          temp[`${i?.to} - ${i?.terminal}`] = true;
        });
      });
      const allStores = Object.keys(temp);
      const mainStoreKey = `${storeData.name} - ${storeData.location}`;
      const otherStores = allStores.filter((s) => s !== mainStoreKey);
      // Prepare header rows
      // Row 1: Terminal names - add empty cells for merging
      const row1 = [];
      row1.push(storeData.location, ""); // Main store terminal
      otherStores.forEach((s) => {
        row1.push(s.split("-")[1].trim(), ""); // Other store terminals
      });

      // Row 2: Store names - add empty cells for merging
      const row2 = [];
      row2.push(storeData.name, ""); // Main store
      otherStores.forEach((s) => {
        row2.push(s.split("-")[0].trim(), ""); // Other stores
      });

      // Row 3: Column headers
      const row3 = [
        "Title",
        "Price",
        ...otherStores.flatMap(() => ["Price", "Diff %"]),
      ];

      // Prepare data rows
      const rows = storeData.items.map((item) => {
        const row = [item.description, item.price];

        // Add comparison data for each other store
        otherStores.forEach((storeKey) => {
          const i = item.compare.find(
            (c) =>
              c.to.trim().toLowerCase() ===
                storeKey.split("-")[0].trim().toLowerCase() &&
              c.terminal.trim().toLowerCase() ===
                storeKey.split("-")[1].trim().toLowerCase()
          );

          if (!i) {
            row.push("-", "-");
          } else {
            row.push(
              i.price,
              (
                parseFloat(i.difference.replace("$", "")) /
                parseFloat(i.price.replace("$", ""))
              ).toFixed(2) + "%"
            );
          }
        });

        return row;
      });

      // Combine all rows
      const ws_data = [row1, row2, row3, ...rows];

      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet(ws_data);

      // Set up merges
      const merges = [];

      // Merge cells in pairs for row 1 (terminal names)
      for (let i = 0; i < row1.length; i += 2) {
        if (i + 1 < row1.length) {
          merges.push({ s: { r: 0, c: i }, e: { r: 0, c: i + 1 } });
        }
      }

      // Merge cells in pairs for row 2 (store names)
      for (let i = 0; i < row2.length; i += 2) {
        if (i + 1 < row2.length) {
          merges.push({ s: { r: 1, c: i }, e: { r: 1, c: i + 1 } });
        }
      }

      // Apply merges
      ws["!merges"] = merges;

      // Generate sheet name (max 31 chars)
      const sheetName = `${storeData.name}-${storeData.location}`
        .replace(/[\\/?*[\]]/g, "") // Remove invalid characters
        .substring(0, 31); // Limit to 31 chars

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    // Generate and download the Excel file
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      `${title}.xlsx`
    );
    setDownloadLoader(false);
    toast.success("Excel downloaded!");
  };

  return (
    <div className={styles["container"]}>
      {/* Heading */}
      <Header 
        onMonthYearChange={handleMonthYearChange}
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
      />
      <hr />

      {/* System Reports. */}
      <div className={styles["system"]}>
        <div className={styles["heading"]}>
          <h3>System Reports</h3>
          <p>
            Our pre-made reports are tailored to help you take action and become
            competitive in the most efficient way.
          </p>
        </div>
        {loading ? (
          <div className="loader-container">
            <PulseLoader />
          </div>
        ) : (
          <div className={styles["reports"]}>
            <Report
              onDownload={handleDownload}
              onDownloadExcel={handleDownloadExcel}
              title="List of all products by store"
            >
              List of all products by store
            </Report>
            <Report
              onDownload={handleDownload}
              onDownloadExcel={handleDownloadExcel}
              title="List of products that are least competitive"
            >
              List of products that are least competitive
            </Report>
            <Report
              onDownload={handleDownload}
              onDownloadExcel={handleDownloadExcel}
              title="List of products that are most competitive"
            >
              List of products that are most competitive
            </Report>
          </div>
        )}
      </div>

      {/* Your Reports. */}
      <div className={styles["your"]}>
        <div className={styles["heading"]}>
          <div className={styles["titleAddReport"]}>
            <h3>Your Reports</h3>
            <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              <div className={styles["sortDropdownWrapper"]}>
                <DropDown
                  steps={sortOptions}
                  selectedValue={sortOrder}
                  onChange={handleSortChange}
                />
              </div>
              <div className={styles["addReport"]} onClick={showAddReport}>
                Add Report
              </div>
            </div>
          </div>
          <p>Generate custom reports as per your requirement.</p>
        </div>
        {loading || yourReportsState.loading ? (
          <div className="loader-container">
            <PulseLoader />
          </div>
        ) : (
          <div className={styles["reports"]}>
            {sortedReports.map((report, sortedIndex) => {
                const originalIndex = yourReportsState.yourReports.findIndex(
                  (r) => r.id === report.id
                );
                return (
                  <Report
                    onDownload={handleDownload}
                    onDownloadExcel={handleDownloadExcel}
                    title={report.name}
                    id={report.id}
                    key={`${report.id}-${sortOrder}`}
                  >
                    {report.name}{" "}
                    <span>
                      <i
                        className="bi bi-pen"
                        onClick={() => showEditReport(originalIndex)}
                      ></i>
                      <i
                        className="bi bi-trash"
                        style={{ fontSize: "2.5rem" }}
                        onClick={() => deleteReport(originalIndex)}
                      ></i>
                    </span>
                  </Report>
                );
              })}
          </div>
        )}
      </div>

      {/* Add report */}
      {addReportState.showAddReport && <AddReportPopup />}

      {/* Edit report */}
      {addReportState.showEditReport.show && <EditReport />}

      {/* <PDFDownloadLink document={<PdfDocument />} fileName="styled-report.pdf">
        {({ loading }) => (loading ? 'Loading document...' : 'Download PDF')}
      </PDFDownloadLink> */}
      {downloadLoader && (
        <div className={styles["loading-wrapper"]}>
          <PulseLoader />
        </div>
      )}
    </div>
  );
};

export default FnbReporting;
